/**
 * SearchPanel — ⌘K command palette / floating search modal.
 *
 * Opens when:
 *   - window fires a `mc:openSearch` CustomEvent, OR
 *   - the `open` prop becomes true.
 *
 * Closes on Escape, backdrop click, or selecting a result.
 *
 * API: GET /api/admin/ops/search?q={query}  (200 ms debounce)
 * Auth: Authorization: Bearer <admin-token>
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Cpu, User, Inbox, ChevronRight } from "lucide-react";

import { getAdminToken } from "@/lib/authStorage";
import { API } from "@/App";

// ── Icon map ──────────────────────────────────────────────────────────────────

const TYPE_ICON = {
  agent:    Cpu,
  expert:   User,
  decision: Inbox,
};

// ── Styles (inline, keyed to --mc-* CSS vars) ─────────────────────────────────

const S = {
  backdrop: {
    position:        "fixed",
    inset:           0,
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex:          200,
    display:         "flex",
    alignItems:      "flex-start",
    justifyContent:  "center",
    paddingTop:      "15vh",
  },
  modal: {
    width:           "100%",
    maxWidth:        560,
    backgroundColor: "var(--mc-bg)",
    border:          "1px solid var(--mc-border)",
    borderRadius:    8,
    boxShadow:       "0 24px 80px -30px rgba(14,20,16,0.45)",
    overflow:        "hidden",
    display:         "flex",
    flexDirection:   "column",
  },
  inputRow: {
    display:         "flex",
    alignItems:      "center",
    gap:             10,
    padding:         "0 16px",
    height:          52,
    flexShrink:      0,
  },
  searchIcon: {
    color:           "var(--color-slate-400)",
    flexShrink:      0,
  },
  input: {
    flex:            1,
    border:          "none",
    outline:         "none",
    background:      "transparent",
    fontFamily:      "var(--font-sans)",
    fontSize:        16,
    color:           "var(--mc-text)",
    minWidth:        0,
  },
  divider: {
    height:          1,
    backgroundColor: "var(--mc-border)",
    flexShrink:      0,
  },
  results: {
    overflowY:       "auto",
    maxHeight:       340,
    scrollbarWidth:  "thin",
  },
  groupHeader: {
    padding:         "8px 16px 4px",
    fontFamily:      "var(--font-mono)",
    fontSize:        10,
    textTransform:   "uppercase",
    letterSpacing:   "0.1em",
    color:           "var(--color-slate-400)",
  },
  resultRow: (isActive) => ({
    display:         "flex",
    alignItems:      "center",
    gap:             10,
    padding:         "9px 16px",
    cursor:          "pointer",
    backgroundColor: isActive ? "rgba(23,128,61,0.12)" : "transparent",
    borderLeft:      isActive ? "2px solid #17803D" : "2px solid transparent",
    transition:      "background-color 100ms",
  }),
  rowIcon: {
    color:           "var(--color-slate-400)",
    flexShrink:      0,
  },
  rowLabel: {
    flex:            1,
    fontFamily:      "var(--font-sans)",
    fontSize:        14,
    color:           "var(--mc-text)",
    overflow:        "hidden",
    textOverflow:    "ellipsis",
    whiteSpace:      "nowrap",
  },
  rowMeta: {
    fontFamily:      "var(--font-mono)",
    fontSize:        12,
    color:           "var(--color-slate-500)",
    flexShrink:      0,
    marginLeft:      "auto",
    maxWidth:        180,
    overflow:        "hidden",
    textOverflow:    "ellipsis",
    whiteSpace:      "nowrap",
  },
  chevron: {
    color:           "var(--color-slate-600)",
    flexShrink:      0,
  },
  footer: {
    height:          36,
    display:         "flex",
    alignItems:      "center",
    gap:             16,
    padding:         "0 16px",
    borderTop:       "1px solid var(--mc-border)",
    flexShrink:      0,
  },
  footerHint: {
    fontFamily:      "var(--font-mono)",
    fontSize:        11,
    color:           "var(--color-slate-500)",
    display:         "flex",
    alignItems:      "center",
    gap:             5,
  },
  footerKbd: {
    fontFamily:      "var(--font-mono)",
    fontSize:        10,
    backgroundColor: "var(--mc-surface)",
    border:          "1px solid var(--mc-border)",
    borderRadius:    3,
    padding:         "1px 5px",
    color:           "var(--mc-text)",
  },
  hintRow: {
    display:         "flex",
    alignItems:      "center",
    justifyContent:  "center",
    height:          40,
    fontFamily:      "var(--font-sans)",
    fontSize:        13,
    color:           "var(--color-slate-500)",
    padding:         "8px 16px",
  },
};

// ── Group results by type ─────────────────────────────────────────────────────

function groupResults(results) {
  const groups = {};
  const ORDER  = ["agent", "expert", "decision"];

  for (const r of results) {
    if (!groups[r.type]) groups[r.type] = [];
    groups[r.type].push(r);
  }

  return ORDER.filter((t) => groups[t]).map((t) => ({ type: t, items: groups[t] }));
}

const TYPE_LABEL = { agent: "AGENTS", expert: "PEOPLE", decision: "DECISIONS" };

// ── Component ─────────────────────────────────────────────────────────────────

export default function SearchPanel({ open: openProp, onClose }) {
  const navigate = useNavigate();

  const [isOpen,    setIsOpen]    = useState(false);
  const [query,     setQuery]     = useState("");
  const [results,   setResults]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const inputRef     = useRef(null);
  const debounceRef  = useRef(null);
  const cancelRef    = useRef(null); // AbortController

  // ── Open/close helpers ───────────────────────────────────────────────────────

  const open = useCallback(() => {
    setIsOpen(true);
    setQuery("");
    setResults([]);
    setLoading(false);
    setActiveIdx(0);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    setResults([]);
    setLoading(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (cancelRef.current)   cancelRef.current.abort();
    onClose?.();
  }, [onClose]);

  // ── Sync with openProp ───────────────────────────────────────────────────────

  useEffect(() => {
    if (openProp) open();
  }, [openProp, open]);

  // ── Listen for mc:openSearch CustomEvent ─────────────────────────────────────

  useEffect(() => {
    const handler = () => open();
    window.addEventListener("mc:openSearch", handler);
    return () => window.removeEventListener("mc:openSearch", handler);
  }, [open]);

  // ── Autofocus when opened ────────────────────────────────────────────────────

  useEffect(() => {
    if (isOpen) {
      // give the DOM a tick to mount before focusing
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // ── Keyboard handler (Escape + arrows + Enter) ───────────────────────────────

  useEffect(() => {
    if (!isOpen) return;

    function handler(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }

      const flatResults = results.flat
        ? results
        : [];

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, results.length - 1));
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        const item = results[activeIdx];
        if (item) {
          navigate(item.route);
          close();
        }
      }
    }

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, results, activeIdx, navigate, close]);

  // ── Search with debounce ─────────────────────────────────────────────────────

  const doSearch = useCallback(async (q) => {
    if (cancelRef.current) cancelRef.current.abort();
    cancelRef.current = new AbortController();

    setLoading(true);
    try {
      const token = getAdminToken();
      const res   = await axios.get(`${API}/admin/ops/search`, {
        params:  { q },
        headers: { Authorization: `Bearer ${token}` },
        signal:  cancelRef.current.signal,
      });
      setResults(res.data?.results ?? []);
      setActiveIdx(0);
    } catch (err) {
      if (axios.isCancel(err) || err?.name === "CanceledError") return;
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleQueryChange = useCallback((e) => {
    const q = e.target.value;
    setQuery(q);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (q.length < 2) {
      if (cancelRef.current) cancelRef.current.abort();
      setResults([]);
      setLoading(false);
      return;
    }

    debounceRef.current = setTimeout(() => doSearch(q), 200);
  }, [doSearch]);

  // ── Select a result ──────────────────────────────────────────────────────────

  const selectResult = useCallback((item) => {
    navigate(item.route);
    close();
  }, [navigate, close]);

  // ── Render ───────────────────────────────────────────────────────────────────

  if (!isOpen) return null;

  const groups = groupResults(results);
  const showMultipleGroups = groups.length > 1;

  // Flatten for index tracking
  const flat = groups.flatMap((g) => g.items);

  // Build a global index offset map per group
  const groupOffsets = [];
  let offset = 0;
  for (const g of groups) {
    groupOffsets.push(offset);
    offset += g.items.length;
  }

  return (
    <div
      style={S.backdrop}
      onClick={close}
      aria-modal="true"
      role="dialog"
      aria-label="Command palette search"
    >
      <div
        style={S.modal}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Input row ── */}
        <div style={S.inputRow}>
          <Search size={18} style={S.searchIcon} aria-hidden="true" />
          <input
            ref={inputRef}
            style={S.input}
            type="text"
            value={query}
            onChange={handleQueryChange}
            placeholder="Search agents, people, decisions…"
            aria-label="Search"
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        {/* ── Divider ── */}
        <div style={S.divider} />

        {/* ── Results body ── */}
        <div style={S.results} role="listbox" aria-label="Search results">
          {/* No query: initial hint */}
          {query.length < 2 && !loading && (
            <div style={S.hintRow}>
              Type to search agents, people, and decisions.
            </div>
          )}

          {/* Loading */}
          {loading && query.length >= 2 && (
            <div
              style={{
                ...S.hintRow,
                animation: "mc-pulse 1.2s ease-in-out infinite",
              }}
            >
              Searching…
            </div>
          )}

          {/* Empty state */}
          {!loading && query.length >= 2 && results.length === 0 && (
            <div style={{ ...S.hintRow, color: "var(--color-slate-400)" }}>
              No results for "{query}"
            </div>
          )}

          {/* Result groups */}
          {!loading && results.length > 0 && groups.map((group, gi) => {
            const groupStart = groupOffsets[gi];

            return (
              <div key={group.type}>
                {showMultipleGroups && (
                  <div style={S.groupHeader} aria-hidden="true">
                    {TYPE_LABEL[group.type] ?? group.type.toUpperCase()}
                  </div>
                )}

                {group.items.map((item, ii) => {
                  const globalIdx = groupStart + ii;
                  const isActive  = globalIdx === activeIdx;
                  const Icon      = TYPE_ICON[item.type] ?? ChevronRight;

                  return (
                    <div
                      key={item.id}
                      style={S.resultRow(isActive)}
                      role="option"
                      aria-selected={isActive}
                      onClick={() => selectResult(item)}
                      onMouseEnter={() => setActiveIdx(globalIdx)}
                    >
                      <Icon size={14} style={S.rowIcon} aria-hidden="true" />
                      <span style={S.rowLabel}>{item.label}</span>
                      {item.meta && (
                        <span style={S.rowMeta}>{item.meta}</span>
                      )}
                      <ChevronRight size={12} style={S.chevron} aria-hidden="true" />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* ── Footer hint ── */}
        <div style={S.footer} aria-hidden="true">
          <span style={S.footerHint}>
            <kbd style={S.footerKbd}>↑</kbd>
            <kbd style={S.footerKbd}>↓</kbd>
            navigate
          </span>
          <span style={S.footerHint}>
            <kbd style={S.footerKbd}>↵</kbd>
            open
          </span>
          <span style={S.footerHint}>
            <kbd style={S.footerKbd}>Esc</kbd>
            close
          </span>
        </div>
      </div>
    </div>
  );
}
